import { ref, onMounted, onBeforeUnmount, type Ref } from 'vue';

/**
 * Tracks the currently visible section anchor via IntersectionObserver.
 *
 * Observes all elements matching the provided selectors (or IDs) and
 * updates `activeAnchor` when a section enters the top third of the
 * viewport (below the sticky nav).
 *
 * @param anchors - Array of anchor IDs to observe (without '#').
 * @returns activeAnchor — reactive ref containing the ID of the current section.
 */
export const useActiveAnchor = (anchors: string[]): { activeAnchor: Ref<string> } => {
  const activeAnchor = ref<string>(anchors[0] ?? '');

  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            activeAnchor.value = entry.target.id;
            break;
          }
        }
      },
      {
        rootMargin: '-56px 0px -60% 0px',
        threshold: 0
      }
    );

    for (const id of anchors) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return { activeAnchor };
};
